import { createSlice, createAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import { UserPronunciationProfile, SpeakingAnalytics, ProgressTimeline } from '@/services/speaking/types';

// === ANALYTICS ACTIONS ===
export const fetchUserProfileRequest = createAction('analytics/fetchUserProfileRequest');
export const fetchUserProfileSuccess = createAction<UserPronunciationProfile>('analytics/fetchUserProfileSuccess');
export const fetchUserProfileFailure = createAction<string>('analytics/fetchUserProfileFailure');

export const fetchAnalyticsRequest = createAction<number | undefined>('analytics/fetchAnalyticsRequest');
export const fetchAnalyticsSuccess = createAction<SpeakingAnalytics>('analytics/fetchAnalyticsSuccess');
export const fetchAnalyticsFailure = createAction<string>('analytics/fetchAnalyticsFailure');

export const fetchProgressTimelineRequest = createAction<number>('analytics/fetchProgressTimelineRequest');
export const fetchProgressTimelineSuccess = createAction<ProgressTimeline>('analytics/fetchProgressTimelineSuccess');
export const fetchProgressTimelineFailure = createAction<string>('analytics/fetchProgressTimelineFailure');

export const setAnalyticsPeriod = createAction<number>('analytics/setAnalyticsPeriod');
export const resetAnalyticsState = createAction('analytics/resetAnalyticsState');

// === STATE INTERFACE ===
export interface AnalyticsState {
    userProfile: UserPronunciationProfile | null;
    analytics: SpeakingAnalytics | null;
    progressTimeline: ProgressTimeline | null;

    // Settings
    analyticsPeriod: number; // days
    timelinePeriod: number; // days

    // Calculated metrics
    overallProgress: {
        currentScore: number;
        targetScore: number;
        progressPercentage: number;
        weeklyGrowth: number;
        monthlyGrowth: number;
    } | null;

    phonemeProgress: {
        improving: Array<{ phoneme: string; trend: number }>;
        struggling: Array<{ phoneme: string; accuracy: number }>;
        mastered: Array<{ phoneme: string; accuracy: number }>;
    } | null;

    streakMetrics: {
        currentStreak: number;
        longestStreak: number;
        streakType: 'daily' | 'weekly' | 'none';
        daysUntilNextMilestone: number;
        motivationalMessage: string;
    } | null;

    performanceInsights: {
        strengths: string[];
        weaknesses: string[];
        recommendations: string[];
        nextFocus: string;
    } | null;

    milestones: {
        completed: Array<{ name: string; date: string; score: number }>;
        upcoming: Array<{ name: string; requirement: number; progress: number }>;
        next: { name: string; requirement: number; timeEstimate: string };
    } | null;

    // Loading States
    isLoadingProfile: boolean;
    isLoadingAnalytics: boolean;
    isLoadingTimeline: boolean;

    // Error States
    profileError: string | null;
    analyticsError: string | null;
    timelineError: string | null;
}

const initialState: AnalyticsState = {
    userProfile: null,
    analytics: null,
    progressTimeline: null,

    analyticsPeriod: 30,
    timelinePeriod: 90,

    overallProgress: null,
    phonemeProgress: null,
    streakMetrics: null,
    performanceInsights: null,
    milestones: null,

    isLoadingProfile: false,
    isLoadingAnalytics: false,
    isLoadingTimeline: false,

    profileError: null,
    analyticsError: null,
    timelineError: null,
};

const analyticsSlice = createSlice({
    name: 'analytics',
    initialState,
    reducers: {
        calculateMetrics: (state) => {
            // Calculate derived metrics when data is available
            if (state.userProfile) {
                // Calculate overall progress
                const currentScore =
                    state.userProfile.phoneme_accuracy * 0.4 +
                    state.userProfile.fluency_score * 0.3 +
                    state.userProfile.prosody_score * 0.3;

                const targetScores: Record<string, number> = {
                    beginner: 60,
                    elementary: 70,
                    intermediate: 80,
                    upper_intermediate: 85,
                    advanced: 90,
                    native_like: 95,
                };

                const targetScore = targetScores[state.userProfile.overall_pronunciation_level] || 70;
                const progressPercentage = Math.min(100, (currentScore / targetScore) * 100);

                // Calculate growth trends
                const weeklyScores = state.userProfile.weekly_scores || [];
                const weeklyGrowth =
                    weeklyScores.length >= 2
                        ? weeklyScores[weeklyScores.length - 1] - weeklyScores[weeklyScores.length - 2]
                        : 0;

                const monthlyProgress = state.userProfile.monthly_progress || [];
                const monthlyGrowth =
                    monthlyProgress.length >= 2
                        ? (monthlyProgress[monthlyProgress.length - 1] as any)?.score -
                              (monthlyProgress[monthlyProgress.length - 2] as any)?.score || 0
                        : 0;

                state.overallProgress = {
                    currentScore: Math.round(currentScore),
                    targetScore,
                    progressPercentage: Math.round(progressPercentage),
                    weeklyGrowth: Math.round(weeklyGrowth * 10) / 10,
                    monthlyGrowth: Math.round(monthlyGrowth * 10) / 10,
                };

                // Calculate streak metrics
                const currentStreak = state.userProfile.streak_days;
                const longestStreak = state.userProfile.longest_streak;

                let streakType: 'daily' | 'weekly' | 'none' = 'none';
                if (currentStreak >= 7) {
                    streakType = 'weekly';
                } else if (currentStreak >= 1) {
                    streakType = 'daily';
                }

                const milestones = [7, 14, 30, 60, 100, 365];
                const nextMilestone = milestones.find((m) => m > currentStreak) || milestones[milestones.length - 1];
                const daysUntilNextMilestone = nextMilestone - currentStreak;

                const messages: Record<number, string> = {
                    0: 'Start your speaking practice streak today! 🚀',
                    1: 'Great start! Keep going to build momentum! 💪',
                    3: "You're building a habit! Three days strong! 🔥",
                    7: "Amazing! You've completed a full week! 🌟",
                    14: "Two weeks of dedication! You're unstoppable! 🎉",
                    30: "One month streak! You're a pronunciation champion! 🏆",
                    60: 'Two months of consistent practice! Incredible! 🥇',
                    100: "100 days! You're in the pronunciation hall of fame! 👑",
                };

                const motivationalMessage =
                    messages[currentStreak] || `${currentStreak} days strong! Keep up the amazing work! 🌟`;

                state.streakMetrics = {
                    currentStreak,
                    longestStreak,
                    streakType,
                    daysUntilNextMilestone,
                    motivationalMessage,
                };
            }

            if (state.analytics) {
                // Calculate phoneme progress
                const improving: Array<{ phoneme: string; trend: number }> = [];
                const struggling: Array<{ phoneme: string; accuracy: number }> = [];
                const mastered: Array<{ phoneme: string; accuracy: number }> = [];

                state.analytics.phoneme_breakdown.forEach((phoneme) => {
                    if (phoneme.accuracy_rate > 90) {
                        mastered.push({
                            phoneme: phoneme.phoneme,
                            accuracy: phoneme.accuracy_rate,
                        });
                    } else if (phoneme.accuracy_rate < 60) {
                        struggling.push({
                            phoneme: phoneme.phoneme,
                            accuracy: phoneme.accuracy_rate,
                        });
                    }

                    if (phoneme.avg_accuracy > 70) {
                        improving.push({
                            phoneme: phoneme.phoneme,
                            trend: 0.1, // Mock improvement trend
                        });
                    }
                });

                state.phonemeProgress = {
                    improving: improving.sort((a, b) => b.trend - a.trend),
                    struggling: struggling.sort((a, b) => a.accuracy - b.accuracy),
                    mastered: mastered.sort((a, b) => b.accuracy - a.accuracy),
                };

                // Generate performance insights
                const strengths: string[] = [];
                const weaknesses: string[] = [];
                const recommendations: string[] = [];

                const { overall_stats } = state.analytics;

                if (overall_stats.average_score > 80) {
                    strengths.push('Excellent overall pronunciation accuracy');
                }

                if (overall_stats.improvement_trend > 0) {
                    strengths.push('Consistent improvement over time');
                } else if (overall_stats.improvement_trend < -2) {
                    weaknesses.push('Recent decline in performance');
                    recommendations.push('Review fundamental pronunciation techniques');
                }

                const strugglingPhonemes = state.analytics.phoneme_breakdown
                    .filter((p) => p.accuracy_rate < 60)
                    .slice(0, 3);

                if (strugglingPhonemes.length > 0) {
                    weaknesses.push(`Difficulty with: ${strugglingPhonemes.map((p) => p.phoneme).join(', ')}`);
                    recommendations.push('Focus on minimal pairs and targeted phoneme practice');
                }

                let nextFocus = 'Continue regular practice';
                if (strugglingPhonemes.length > 0) {
                    nextFocus = `Focus on ${strugglingPhonemes[0].phoneme} sound`;
                } else if (overall_stats.average_score < 75) {
                    nextFocus = 'Work on overall pronunciation clarity';
                } else {
                    nextFocus = 'Practice advanced prosody and intonation';
                }

                state.performanceInsights = {
                    strengths,
                    weaknesses,
                    recommendations,
                    nextFocus,
                };
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)
            .addCase(resetAnalyticsState, () => initialState)

            // === FETCH USER PROFILE ===
            .addCase(fetchUserProfileRequest, (state) => {
                state.isLoadingProfile = true;
                state.profileError = null;
            })
            .addCase(fetchUserProfileSuccess, (state, action) => {
                state.isLoadingProfile = false;
                state.userProfile = action.payload;
            })
            .addCase(fetchUserProfileFailure, (state, action) => {
                state.isLoadingProfile = false;
                state.profileError = action.payload;
            })

            // === FETCH ANALYTICS ===
            .addCase(fetchAnalyticsRequest, (state, action) => {
                state.isLoadingAnalytics = true;
                state.analyticsError = null;
                state.analyticsPeriod = action.payload || 30;
            })
            .addCase(fetchAnalyticsSuccess, (state, action) => {
                state.isLoadingAnalytics = false;
                state.analytics = action.payload;
            })
            .addCase(fetchAnalyticsFailure, (state, action) => {
                state.isLoadingAnalytics = false;
                state.analyticsError = action.payload;
            })

            // === FETCH PROGRESS TIMELINE ===
            .addCase(fetchProgressTimelineRequest, (state, action) => {
                state.isLoadingTimeline = true;
                state.timelineError = null;
                state.timelinePeriod = action.payload;
            })
            .addCase(fetchProgressTimelineSuccess, (state, action) => {
                state.isLoadingTimeline = false;
                state.progressTimeline = action.payload;
            })
            .addCase(fetchProgressTimelineFailure, (state, action) => {
                state.isLoadingTimeline = false;
                state.timelineError = action.payload;
            })

            // === SETTINGS ===
            .addCase(setAnalyticsPeriod, (state, action) => {
                state.analyticsPeriod = action.payload;
            });
    },
});

export const { calculateMetrics } = analyticsSlice.actions;

export default analyticsSlice.reducer;
